import {
    createContext,
    MutableRefObject,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {EventEmitter, first, selectMany, sum} from "nate-react-api-helpers";
import {SortByCol, SortFx, TablePersonalizeContext} from "./TablePersonalize";
import {Column, Group, TableSelectionContext, TID} from "./Table";
import {useQueryParam} from "../../pages/project/quote/pricing/QueryParam";
import {useSyncedRef} from "../SyncedRef";

export const FilterContext = createContext({
    valueRef: {} as {[k: string]: (input: any) => boolean},
    sortedRef: {current: []} as MutableRefObject<Group<any>[]>,
    onClear: new EventEmitter(),
    onFilter: new EventEmitter<FilterDetail>(),
    srcData: new EventEmitter<any[]>(),

    // per-column filter text persisted in the URL query string so it survives
    // navigate-away-then-back (browser history). columnFilterInit seeds a cell's
    // initial value; setColumnFilter writes the latest value back (debounced).
    columnFilterInit: {} as {[columnName: string]: string},
    setColumnFilter: (columnName: string, value: string) => {},
})

export type FilterDetail = {total: number, visible: number}
type FilterFx<T> = (input: T) => boolean;

type FilterSpec = {
    type: "eq",
    search: {
        [key: string]: string | number;
    }
} | {
    type: "contains",
    search: {
        [key: string]: (string | number)[];
    }
}

export function buildFilterLink(input: {
    tableName: string;
    filter: FilterSpec // key => value
}) {
    let p = new URLSearchParams();
    p.append(input.tableName, JSON.stringify(input.filter));
    return p.toString();
}

export function FilterProvider<T extends TID>(props: PropsWithChildren<{
    tableName: string;
    data: Group<T>[];
    groupFilter: (input: string) => boolean;
    columns: Column<T>[]
}>) {
    const onClear = useMemo(() => new EventEmitter(), []);
    const onFilter = useMemo(() => new EventEmitter<FilterDetail>(), []);

    const [filter, setFilter] = useState<FilterFx<T>[]>([]);
    const {sortBy, customSort, multiSortBy} = useContext(TablePersonalizeContext);
    const value = useRef({}as {[key: string]: FilterFx<T>});

    const searchParamsFilterInit = useRef(false);
    const [searchParams, setSearchParams] = useQueryParam(props.tableName, "");

    // auto clear search params if filters are changed
    useEffect(() => {
        if(!searchParamsFilterInit.current) return;
        setSearchParams("")
    }, [filter, props.tableName, setSearchParams])

    useEffect(() => {
        const sub = onClear.subscribe(() => setSearchParams(""))
        return () => sub.cancel();
    }, [onClear, setSearchParams])

    // per-column filter text, persisted in the URL under "<tableName>~cf" as a
    // JSON {columnName: text} map. Kept in browser history via replace-semantics
    // so a keystroke doesn't flood the back button.
    const [columnFilterParam, setColumnFilterParam] = useQueryParam(props.tableName + "~cf", "", {replace: true});

    const columnFilterInit = useMemo(() => {
        if(!columnFilterParam) return {} as {[k: string]: string};
        try {
            return JSON.parse(columnFilterParam) as {[k: string]: string};
        } catch (e) {
            return {} as {[k: string]: string};
        }
    }, [columnFilterParam]);

    // live map of current cell values; seeded once from the URL, then mutated as
    // cells change. Writes to the URL are debounced.
    const columnFilterMap = useRef<{[k: string]: string}>({});
    const columnFilterSeeded = useRef(false);
    if(!columnFilterSeeded.current) {
        columnFilterMap.current = Object.assign({}, columnFilterInit);
        columnFilterSeeded.current = true;
    }

    const columnFilterWriteTimer = useRef<any>(null);
    const columnFilterParamRef = useSyncedRef(columnFilterParam);
    const setColumnFilter = useCallback((columnName: string, value: string) => {
        const map = columnFilterMap.current;
        if(value) {
            map[columnName] = value;
        } else {
            delete map[columnName];
        }

        if(columnFilterWriteTimer.current) clearTimeout(columnFilterWriteTimer.current);
        columnFilterWriteTimer.current = setTimeout(() => {
            const keys = Object.keys(map);
            const next = keys.length > 0 ? JSON.stringify(map) : "";
            if(next === columnFilterParamRef.current) return;
            setColumnFilterParam(next);
        }, 400);
    }, [setColumnFilterParam, columnFilterParamRef]);

    // a pending write must not navigate back to this page after the user has left it
    useEffect(() => {
        return () => clearTimeout(columnFilterWriteTimer.current);
    }, []);

    useEffect(() => {
        const sub = onClear.subscribe(() => {
            columnFilterMap.current = {};
            if(columnFilterWriteTimer.current) clearTimeout(columnFilterWriteTimer.current);
            setColumnFilterParam("");
        });
        return () => sub.cancel();
    }, [onClear, setColumnFilterParam]);

    const columnsRef = useSyncedRef(props.columns);

    useEffect(() => {
        if(!searchParams) {
            delete value.current.fromSearchParams;
            return;
        }

        let filter = JSON.parse(searchParams) as FilterSpec;
        const filterFx = (input: T) => {
            if(filter.type === "eq") {
                for(let i in filter.search) {
                    // @ts-ignore
                    if(input[i] !== filter.search[i]) return false;
                }
            } else if(filter.type === "contains") {
                for(let i in filter.search) {
                    // @ts-ignore
                    if(filter.search[i].indexOf(input[i]) === -1) return false;
                }
            }

            return true;
        };

        value.current = {
            fromSearchParams: filterFx,
        }

        setFilter([filterFx]);

        setTimeout(() => {
            searchParamsFilterInit.current = true;
        }, 100)
    }, [props.tableName, searchParams, columnsRef])

    const data = props.data;

    const srcData = useMemo(() => new EventEmitter<T[]>(), []);
    useEffect(() => {
        srcData.emit(selectMany(data, d => d.rows));
    }, [srcData, data]);

    const originalSortOrder = useRef(data);

    useEffect(() => {
        originalSortOrder.current = data.map(d => ({
            key: d.key,
            rows: d.rows.slice(0),
        }));
    }, [data]);

    let sortFx = useSortFx({
        columns: props.columns as any,
        sortBy: sortBy,
        multiSortBy: multiSortBy,
        customSort: customSort,
    });

    const sorted = useMemo(() => {
        const findIndex = (id: number) => {
            let sum = 0;

            for(let i = 0; i < originalSortOrder.current.length; i++) {
                const g = originalSortOrder.current[i];
                const index = g.rows.findIndex(r => r.id === id);
                if(index === -1) {
                    sum += g.rows.length;
                    continue;
                }

                return sum + index;
            }

            return -1;
        }

        const sortFx2 = (a: T, b: T) => {
            const aIndex = findIndex(a.id)
            const bIndex = findIndex(b.id);

            return aIndex - bIndex;
        }

        let sorter = sortFx || sortFx2;

        return data.map(v => {
            v.rows = v.rows.slice(0);
            v.rows.sort(sorter)
            return v;
        })
    }, [sortFx, data]);

    const sortedRef = useSyncedRef(sorted);

    const groupFilter = props.groupFilter;
    const filteredAndSorted = useMemo(() => {
        let dt = sorted.filter(v => groupFilter(v.key))

        if(filter.length > 0) {
            return dt.map(grp => {
                return Object.assign({}, grp, {
                    rows: grp.rows.filter(d => {
                        for(let i = 0; i < filter.length; i++) {
                            if(!filter[i](d)) return false;
                        }

                        return true;
                    }),
                })
            })
        }

        return dt
    }, [sorted, filter, groupFilter]);

    useEffect(() => {
        onFilter.emit({
            total: sum(props.data.map(d => d.rows.length)),
            visible: sum(filteredAndSorted.map(d => d.rows.length)),
        });
    }, [filteredAndSorted, props.data, onFilter]);

    const {onFilteredData} = useContext(TableSelectionContext);
    useEffect(() => {
        onFilteredData(props.tableName, selectMany(filteredAndSorted, d => d.rows));
    }, [props.tableName, filteredAndSorted, onFilteredData]);

    const filterRef = useRef(filter);
    filterRef.current = filter;

    useEffect(() => {
        const int = setInterval(() => {
            const newValues = Object.values(value.current);

            if(newValues.length === filterRef.current.length) {
                const hasChange = filterRef.current.filter((v, index) => newValues[index] !== v).length > 0;
                if(!hasChange) return;
            }

            setFilter(newValues);
        }, 800);

        return () => clearInterval(int);
    }, []);

    const ctx = useMemo(() => ({
        valueRef: value.current,
        sortedRef: sortedRef,
        onClear: onClear,
        onFilter: onFilter,
        srcData: srcData,
        columnFilterInit: columnFilterInit,
        setColumnFilter: setColumnFilter,
    }), [value, onClear, onFilter, srcData, sortedRef, columnFilterInit, setColumnFilter]);

    return (
        <FilterContext.Provider value={ctx}>
            <FilterContextPrivate.Provider value={filteredAndSorted}>
                {props.children}
            </FilterContextPrivate.Provider>
        </FilterContext.Provider>
    )
}

export const FilterContextPrivate = createContext([] as Group<any>[]);

function useSortFx(input: {
    customSort?: SortFx;
    sortBy?: SortByCol | null;
    multiSortBy?: SortByCol[] | null;
    columns: Column<any>[];
}) {
    const {sortBy, customSort, multiSortBy, columns} = input;

    return useMemo(() => {
        if(customSort) return customSort;
        if(multiSortBy) {
            const each = multiSortBy.map((col) => {
                const column = first(columns, c => c.name === col.column);
                const sortFx = column?.sort;
                if(sortFx && col.reverse) {
                    return (a, b) => -1 * sortFx(a, b)
                }

                return sortFx;
            }).filter(v => !!v) as SortFx[];

            return (a, b) => {
                for(let i = 0; i < each.length; i++) {
                    const result = each[i](a, b);
                    if(result !== 0) return result;
                }

                return 0;
            }
        }

        if(sortBy) {
            const col = first(columns, c => c.name === sortBy.column);
            const sortFx = col?.sort;
            if(sortFx) {
                if(sortBy.reverse) {
                    return (a, b) => -1 * sortFx(a, b) as any;
                }

                return sortFx;
            }
        }

        return undefined;
    }, [customSort, multiSortBy, sortBy, columns])
}